import React, { useState } from 'react';
import { useResetPassword } from '../../api/users';
import { useLoginModalContext } from '../../context/LoginModalContext';
import { useNotifications } from '../../context/NotificationContext';
import * as Sentry from '@sentry/react';
import { X } from 'lucide-react';
import './LoginModal.css';

interface LoginModalResetPasswordProps {
  onClose: () => void;
}

const LoginModalResetPassword: React.FC<LoginModalResetPasswordProps> = ({ 
  onClose
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const resetPasswordMutation = useResetPassword();
  const { setCurrentStep, email, setEmail } = useLoginModalContext();
  const { showError, showSuccess } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (email.trim()) {
      try {
        await resetPasswordMutation.mutateAsync({ 
          email: email.trim(), 
        });
        
        showSuccess('Reset code sent to your email address.');
        
        setCurrentStep('reset-password-code');
      } catch (error: any) {
        console.error('Reset password failed:', error);
        
        if (error?.response?.status === 404) {
          setErrorMessage('No user found with this email address.');
        } else {
          Sentry.captureException(error);
          setErrorMessage('Failed to send reset code. Please try again.');
        }
      }
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <h1 className="modal-title">Reset Password</h1>
          
          <p className="modal-description">
            Enter your email address and we'll send you a code to reset your password.
          </p>
          
          {errorMessage && (
            <div className="message message--error">
              {errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="hello@example.com"
                required
                disabled={resetPasswordMutation.isPending}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn--primary btn--login"
              disabled={resetPasswordMutation.isPending || !email.trim()}
            >
              {resetPasswordMutation.isPending ? 'Sending...' : 'Send Reset Code'}
            </button>
          </form>

          <p className="login-link">
            Remember your password? <button 
              type="button" 
              className="login-link-text" 
              onClick={() => setCurrentStep('email')}
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    </>
  );
};

export default LoginModalResetPassword;
